import { uniq, uniqueId } from 'lodash';

import {
  type AllInstructionsData,
  EMPTY_ALL_INSTRUCTIONS_DATA,
  type TovInput,
} from 'src/data/generation';
import { type GenerationTool, type SubGenerationToolProps } from 'src/data/generationTool';
import { type Entity } from 'src/lib/collection';
import { parseJson } from 'src/lib/common';

import { DEFAULT_IMAGE_STYLE_ID, IMAGE_ASPECT_RATIO, type ImageOverlayProps } from '../image';

/** The "Acquisition campaign" multi-prompt template: Home's ads button and the Editor rail both open it. */
export const ACQUISITION_CAMPAIGN_PROMPT_ID = 919;

export interface MultiPromptTemplate extends Entity {
  name: string;
  description?: string;
  info?: string;
  fields?: unknown[];
  blocks?: MultiPromptTemplateBlock[];
  status?: string;
  templateGroup?: string;
  templateType?: string;
  templateOutputType?: string;
  tags?: string[];
  smallImage?: string;
  bigImage?: string;
}

interface MultiPromptTemplateBlock extends Entity {
  type: GenerationTool;
  name: string;
}

export const TEMPLATE_OUTPUT_TYPE = {
  textOnly: 'text_only',
  textAndImage: 'text_and_image',
};

export type TemplateOutputType = (typeof TEMPLATE_OUTPUT_TYPE)[keyof typeof TEMPLATE_OUTPUT_TYPE];

export const doesTemplateOutputIncludeImages = (templateOutputType: TemplateOutputType): boolean =>
  templateOutputType === TEMPLATE_OUTPUT_TYPE.textAndImage;

export interface TemplateSubToolProps {
  generationTool: GenerationTool;
  blockId: string;
  stepName: string;
  /** What this step tells the model to write. Absent for tools whose prompts live server-side. */
  allInstructionsData?: AllInstructionsData;
  /** Position in the template, so a reordered step can say where it moved to. */
  order?: number;
  isImageGenerationEnabled?: boolean;
  imageStyleId?: string;
  imageAspectRatio?: string;
  imageOverlayProperties?: ImageOverlayProps;
  tovInput?: TovInput;
  structureInstructionsData?: AllInstructionsData;
  subGenerationToolsProps?: Record<GenerationTool, SubGenerationToolProps>;
}

/**
 * A step added to an outline by hand. It starts with an empty prompt — the user writes it — and an
 * image step starts switched on, since asking for one and getting nothing would be surprising.
 *
 * An image step also takes the same defaults a template's own image block is read with. A run reads
 * a step's image settings from the step alone — the mutation carries no run-level image settings to
 * fall back on — and the aspect-ratio control shows the project's own value where the step names
 * none, so a step left unset would generate without a ratio while the panel says otherwise.
 *
 * A text step's `allInstructionsData` is the empty structure rather than absent. Absent means
 * something else entirely: a step whose prompt lives server-side, which is drawn without an editor
 * and sent without a prompt. A step nobody can write a prompt for, in a payload the backend then
 * reads a prompt out of, fails the whole run.
 */
export const createTemplateSubToolProps = ({
  generationTool,
  stepName,
  isImage,
  tovInput,
}: {
  generationTool: GenerationTool;
  stepName: string;
  isImage: boolean;
  /** The run's tone. Every other step carries one, and a step that omits it is generated toneless. */
  tovInput?: TemplateSubToolProps['tovInput'];
}): TemplateSubToolProps => ({
  generationTool,
  stepName,
  blockId: uniqueId('block-'),
  allInstructionsData: EMPTY_ALL_INSTRUCTIONS_DATA,
  ...(isImage
    ? {
        isImageGenerationEnabled: true,
        imageStyleId: DEFAULT_IMAGE_STYLE_ID,
        imageAspectRatio: IMAGE_ASPECT_RATIO.ratio1x1,
        imageOverlayProperties: {},
      }
    : {
        structureInstructionsData: undefined,
        tovInput,
      }),
});

/**
 * `order` is what tells a run where each step ended up, so every one of these rewrites it across the
 * whole list rather than touching only the steps that moved. A list whose positions are not
 * contiguous would still render correctly and generate in the wrong order.
 */
const withContiguousOrder = (steps: TemplateSubToolProps[]): TemplateSubToolProps[] =>
  steps.map((step, index) => ({ ...step, order: index }));

/** Moves a step by `offset` places. A move that would leave the list is refused. */
export const moveTemplateStep = (
  steps: TemplateSubToolProps[],
  blockId: string,
  offset: number
): TemplateSubToolProps[] => {
  const from = steps.findIndex((step) => step.blockId === blockId);
  const to = from + offset;
  if (from < 0 || to < 0 || to >= steps.length) {
    return steps;
  }

  const moved = [...steps];
  moved.splice(to, 0, ...moved.splice(from, 1));

  return withContiguousOrder(moved);
};

export const removeTemplateStep = (
  steps: TemplateSubToolProps[],
  blockId: string
): TemplateSubToolProps[] => withContiguousOrder(steps.filter((step) => step.blockId !== blockId));

/** Inserts a step directly after `afterBlockId`; an unknown id appends. */
export const insertTemplateStepAfter = (
  steps: TemplateSubToolProps[],
  afterBlockId: string,
  step: TemplateSubToolProps
): TemplateSubToolProps[] => {
  const after = steps.findIndex((item) => item.blockId === afterBlockId);
  const next = [...steps];
  next.splice(after < 0 ? steps.length : after + 1, 0, step);

  return withContiguousOrder(next);
};

export interface SubToolParams {
  blockId: string;
  newValue: AllInstructionsData;
  isMixture?: boolean;
  subToolKey?: GenerationTool;
}

export interface TemplatePromptInstructionItem {
  tool: GenerationTool;
  title: string;
  structureInstructionsData: AllInstructionsData;
}
export interface TemplateAdvancedSettingsDraft {
  promptInstructionItems: TemplatePromptInstructionItem[];
  subToolProps: TemplateSubToolProps[];
}

export const getMultiPromptTemplateBlocks = (
  template: Pick<MultiPromptTemplate, 'blocks'>
): MultiPromptTemplateBlock[] => {
  if (!template?.blocks) {
    return [];
  }

  if (Array.isArray(template.blocks)) {
    return template.blocks;
  }

  return parseJson<MultiPromptTemplateBlock[]>(template.blocks) || [];
};

export const getMultiPromptTemplateBlockTypes = (
  template: Pick<MultiPromptTemplate, 'blocks'>
): GenerationTool[] => {
  if (!template?.blocks) {
    return [];
  }

  const blocks = getMultiPromptTemplateBlocks(template);
  const blockTypes = blocks.map((block) => block.type);
  return uniq(blockTypes);
};
